/**
 * Component: HomePage
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01, FRONTEND-WEB-V1-R11, FRONTEND-WEB-V1-R12,
 *               FRONTEND-WEB-V1-R13, FRONTEND-WEB-V1-R14, GALEN-CHAT-V1-R01,
 *               SEARCH-HYBRID-V1-R02, SEARCH-SEMANTIC-V1-R01, SEARCH-SIMPLE-V1-R01
 *
 * Home page with mode selection for all four primary search/chat interfaces.
 * FRONTEND-WEB-V1-R01: Provides access to all four interaction modes.
 * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules via configuration feature flags.
 * FRONTEND-WEB-V1-R12: Fully hide disabled modules rather than grey them out.
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useMemo } from 'react';
import { useFeatureFlags } from '@/contexts';
import type { FeatureFlags } from '@/types';

/**
 * Mode card configuration with feature flag mapping
 */
interface ModeConfig {
  title: string;
  description: string;
  href: string;
  featureKey: keyof FeatureFlags;
}

/**
 * All available interaction modes with their feature flag mappings
 * FRONTEND-WEB-V1-R11: Each mode is tied to a feature flag for runtime toggling
 */
const allModes: ModeConfig[] = [
  {
    title: 'Galen Chat',
    description: 'Ask questions and get AI-generated answers with citations from clinical guidelines.',
    href: '/chat',
    featureKey: 'chat',
  },
  {
    title: 'Galen Search',
    description: 'Search with AI-enhanced results combining semantic search and summarization.',
    href: '/search/hybrid',
    featureKey: 'galenSearch',
  },
  {
    title: 'Semantic Search',
    description: 'Find relevant guideline sections using natural language queries.',
    href: '/search/semantic',
    featureKey: 'semanticSearch',
  },
  {
    title: 'Simple Search',
    description: 'Traditional keyword-based search for exact matches.',
    href: '/search/simple',
    featureKey: 'simpleSearch',
  },
];

export function HomePage(): React.ReactElement {
  const { flags, isLoading } = useFeatureFlags();

  /**
   * Filter modes based on feature flags
   * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules
   * FRONTEND-WEB-V1-R12: Fully hide disabled modules (not greyed out)
   */
  const enabledModes = useMemo(() => {
    return allModes.filter((mode) => flags[mode.featureKey]);
  }, [flags]);

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-0">
      <section aria-labelledby="welcome-heading" className="text-center">
        {/* FRONTEND-WEB-V1-R13: Responsive typography */}
        <h2
          id="welcome-heading"
          className="mb-4 text-2xl font-bold tracking-tight sm:text-3xl"
        >
          Welcome to Galen Portal
        </h2>
        <p className="mb-8 text-base text-muted-foreground sm:text-lg">
          AI-Assisted Clinical Guideline Retrieval System
        </p>
      </section>

      <section aria-labelledby="modes-heading" className="mt-12">
        <h3 id="modes-heading" className="sr-only">
          Available interaction modes
        </h3>
        {/* FRONTEND-WEB-V1-R01: Four primary interaction modes */}
        {/* FRONTEND-WEB-V1-R11: Only show enabled modules based on feature flags */}
        {/* FRONTEND-WEB-V1-R12: Disabled modules are fully hidden, not greyed out */}
        {/* FRONTEND-WEB-V1-R13: Responsive grid - single column on mobile, two on desktop */}
        {isLoading ? (
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-32 animate-pulse rounded-lg border bg-muted"
                aria-label="Loading"
              />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            {enabledModes.map((mode) => (
              <ModeCard
                key={mode.href}
                title={mode.title}
                description={mode.description}
                href={mode.href}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

interface ModeCardProps {
  title: string;
  description: string;
  href: string;
}

function ModeCard({
  title,
  description,
  href,
}: ModeCardProps): React.ReactElement {
  return (
    // FRONTEND-WEB-V1-R13: Touch-friendly card with responsive padding
    <a
      href={href}
      className="block rounded-lg border p-4 sm:p-6 transition-colors hover:border-primary hover:bg-accent cursor-pointer focus-ring min-h-touch"
    >
      <h4 className="mb-2 text-base font-semibold sm:text-lg">{title}</h4>
      <p className="text-sm text-muted-foreground">{description}</p>
    </a>
  );
}
